import { Box, darken, IconButton, Theme, Typography, useTheme } from "@mui/material";
import HomeIcon from '@mui/icons-material/Home';
import { Settings } from "@mui/icons-material";

export interface ButtonBar<T> {
  select: boolean;
  text: string;
  args: T;
}

interface Props<T> {
  buttons: ButtonBar<T>[];
  onClick?: (i: number, args: T) => void;
  onHome?: () => void;
  homeIcon?: React.ElementType;
  onConfig?: () => void;
  configIcon?: React.ElementType;
}

interface ButtonNow<T> extends ButtonBar<T> {
  index: number;
}

export const appBarHeight = 8;

export const BarNavigation = <T,>(props: Props<T>): JSX.Element => {
  const theme = useTheme();

  const buttonWidth = appBarHeight * 17.6;
  const buttonSpacing = 5;

  const buttonBefore: ButtonNow<T>[] = [];
  const buttonAfter: ButtonNow<T>[] = [];
  let buttonSected: undefined | ButtonNow<T> = undefined;

  for (const i in props.buttons) {
    const btn = props.buttons[i];
    const index = parseInt(i);

    if (btn.select) {
      buttonSected = { ...btn, index };
      continue;
    }
    if (buttonSected) {
      buttonAfter.push({ ...btn, index });
      continue;
    }
    buttonBefore.push({ ...btn, index });
  }

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "row",
      fontSize: `${appBarHeight}px`,
      backgroundColor: "background.paper",
    }}>
      {/* Home Button */}
      {props.onHome && <Box sx={{
        backgroundColor: "primary.main",
        width: `${buttonSpacing}px`,
      }} />}
      {props.onHome && <Box sx={{
        backgroundColor: "primary.main",
        justifyContent: 'center',
        alignItems: 'center',
        display: "flex",
      }}>
        <IconButton size="small" onClick={props.onHome} >
          {
            props.homeIcon ?
              <props.homeIcon sx={{ color: theme.palette.background.paper }} />
              :
              <HomeIcon sx={{ color: theme.palette.background.paper }} />
          }
        </IconButton>
      </Box>}

      {/* Center buttons */}
      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "row",
        fontSize: `${appBarHeight}px`,
        backgroundColor: "background.paper",
        overflow: "auto",
      }}>

        {/* Box 1 */}
        <Box sx={{
          width: `${(buttonBefore.length + 1) * buttonSpacing + buttonWidth * buttonBefore.length}px`,
          backgroundColor: "primary.main",
          borderBottomRightRadius: "1em",
          justifyContent: 'center',
          alignItems: 'center',
          display: "flex",
        }}>
          <div style={{ width: `${buttonSpacing}px` }} />

          {buttonBefore.map((data, i) => {
            return (
              <div key={i} style={{ display: "flex" }}>
                <Box onClick={() => props.onClick ? props.onClick(data.index, data.args) : undefined} style={{
                  cursor: 'pointer',
                  height: "4em",
                  backgroundColor: darken(theme.palette.primary.main, 0.2),
                  color: "primary.contrastText",
                  minWidth: `${buttonWidth}px`,
                  display: "flex",
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: "1em",
                }}>
                  <Typography fontWeight={"bold"} fontSize={'1.3em'} sx={{
                    color: "primary.contrastText",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {data.text}
                  </Typography>
                </Box>
                {i !== (buttonBefore.length - 1) && <div style={{ width: `${buttonSpacing}px` }} />}
              </div>
            );
          })}


          <div style={{
            width: `${buttonSpacing}px`
          }} />
        </Box >

        {/* Botão do centro */}
        {buttonSected && <CenterBox
          button={buttonSected}
          btnWidth={buttonWidth}
          spacing={buttonSpacing}
          theme={theme}
          onClick={(i) => (buttonSected && props.onClick) ? props.onClick(i, buttonSected.args) : undefined}
        />}

        {/* Box 2 */}
        <Box sx={{
          width: `${(buttonAfter.length + 1) * buttonSpacing + buttonWidth * buttonAfter.length}px`,
          backgroundColor: "primary.main",
          borderBottomLeftRadius: "1em",
          justifyContent: 'left',
          alignItems: 'center',
          display: "flex",
          flex: 1,
        }}>
          <div style={{ width: `${buttonSpacing}px` }} />

          {buttonAfter.map((data, i) => {
            return (
              <div key={i} style={{ display: "flex" }}>
                <Box onClick={() => props.onClick ? props.onClick(data.index, data.args) : undefined} style={{
                  cursor: 'pointer',
                  height: "4em",
                  backgroundColor: darken(theme.palette.primary.main, 0.2),
                  color: "primary.contrastText",
                  minWidth: `${buttonWidth}px`,
                  display: "flex",
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: "1em",
                }}>
                  <Typography fontWeight={"bold"} fontSize={'1.3em'} sx={{
                    color: "primary.contrastText",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {data.text}
                  </Typography>
                </Box>

                {i !== (buttonAfter.length - 1) && <div style={{ width: `${buttonSpacing}px` }} />}
              </div>
            );
          })}


          <div style={{ width: `${buttonSpacing}px` }} />
        </Box >

      </Box>

      {/* Config Button */}
      {props.onConfig && <Box sx={{
        backgroundColor: "primary.main",
        width: `${buttonSpacing}px`,
      }} />}
      {props.onConfig && <Box sx={{
        backgroundColor: "primary.main",
        justifyContent: 'center',
        alignItems: 'center',
        display: "flex",
      }}>
        <IconButton size="small" onClick={props.onConfig} >
          {
            props.homeIcon ?
              <props.homeIcon sx={{ color: theme.palette.background.paper }} />
              :
              <Settings sx={{ color: theme.palette.background.paper }} />
          }
        </IconButton>
      </Box>}
      {props.onConfig && <Box sx={{
        backgroundColor: "primary.main",
        width: `${buttonSpacing}px`,
      }} />}
    </Box >
  );
};


interface PropsCenter<T> {
  button: ButtonNow<T>;
  btnWidth: number;
  spacing: number;
  theme: Theme;
  onClick?: (i: number) => void;
}

const CenterBox = <T,>(props: PropsCenter<T>): JSX.Element => {
  const textColor = props.theme.palette.getContrastText(props.theme.palette.background.default);

  return (
    < Box sx={{
      display: "flex",
      flexDirection: "column",
      width: `${props.spacing * 1 + props.btnWidth}px`,
      backgroundColor: "primary.main",
    }}>
      <Box sx={{
        height: "0.5em",
        backgroundColor: "primary.main",
      }} />

      <Box sx={{
        backgroundColor: "background.default",
        height: "5em",
        borderTopRightRadius: "1em",
        borderTopLeftRadius: "1em",
        paddingTop: "0.45em",
      }}>
        <Box onClick={() => { if (props.onClick) props.onClick(props.button.index); }} style={{
          padding: "1em",
          cursor: 'pointer',
          backgroundColor: "transparent",
          height: "3.5em",
          minWidth: `${props.btnWidth}px`,
          display: "flex",
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Typography fontWeight={"bold"} fontSize={"1.3em"} sx={{
            color: textColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {props.button.text}
          </Typography>
        </Box>
      </Box>
    </Box >
  );
};